// src/components/assistant/mentions.ts
//
// Menciones del compositor del chat: `@agente`, `/skill`, `#herramienta`.
// Cómo viaja cada una (contrato con Orión, services/chat_mentions.py):
//   @agente      → assistant_id (este mensaje y los siguientes hasta quitarlo)
//   /skill       → skill: <slug de OrgWorkflow> (solo ese mensaje)
//   #herramienta → focus_tool_ids: [tool_id…] (solo ese mensaje, máx. 5)
// Las listas ya vienen filtradas por el backend (org y usuario del JWT): aquí
// solo se cargan una vez por página y se filtran por texto.

import { apiUrl } from '@/config';
import { djangoApiCall } from '@/lib/api';

export type MentionKind = 'agent' | 'skill' | 'tool';

export interface MentionItem {
  kind: MentionKind;
  id: string;
  label: string;
  description?: string;
  icon?: string;
}

export const TRIGGERS: Record<string, MentionKind> = { '@': 'agent', '/': 'skill', '#': 'tool' };
export const MAX_TOOLS = 5;

/** Token activo bajo el cursor: el disparador debe ir al inicio o tras un espacio. */
export function activeTrigger(text: string, caret: number): { kind: MentionKind; query: string; start: number } | null {
  const before = text.slice(0, caret);
  const m = /(^|\s)([@/#])([^\s@/#]{0,40})$/.exec(before);
  if (!m) return null;
  const kind = TRIGGERS[m[2]];
  return { kind, query: m[3], start: before.length - m[3].length - 1 };
}

function norm(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

export function filterItems(items: MentionItem[], query: string, limit = 8): MentionItem[] {
  const q = norm(query.trim());
  if (!q) return items.slice(0, limit);
  const starts: MentionItem[] = [];
  const contains: MentionItem[] = [];
  for (const it of items) {
    const l = norm(it.label);
    const id = norm(it.id);
    if (l.startsWith(q) || id.startsWith(q)) starts.push(it);
    else if (l.includes(q) || id.includes(q) || norm(it.description || '').includes(q)) contains.push(it);
  }
  return [...starts, ...contains].slice(0, limit);
}

// ── Carga (una vez por página; el ChatPane se re-monta en cada «Nuevo chat») ──

let _skills: Promise<MentionItem[]> | null = null;
const _tools = new Map<string, Promise<MentionItem[]>>();

interface WorkflowRow {
  slug: string;
  title?: string;
  description?: string;
  when_to_use?: string;
  agent_readable?: boolean;
  status?: string;
}

/** Skills/workflows que el agente puede abrir (agent_readable y activos). */
export function loadSkills(): Promise<MentionItem[]> {
  let p = _skills;
  if (!p) {
    p = _skills = djangoApiCall<WorkflowRow[]>('/admin/company/workflows?agent_readable=true')
      .then((rows) =>
        (Array.isArray(rows) ? rows : [])
          .filter((w) => w?.slug && w.agent_readable !== false)
          .map((w) => ({
            kind: 'skill' as const,
            id: w.slug,
            label: w.title || w.slug,
            description: (w.when_to_use || w.description || '').slice(0, 140),
          })),
      )
      .catch(() => {
        _skills = null; // sin permiso o sin red: sin sugerencias, el chat sigue
        return [];
      });
  }
  return p;
}

/** Tools que este usuario puede usar en el chat (GET /threads/tools de Orión). */
export function loadTools(headers: Record<string, string>, focus?: string): Promise<MentionItem[]> {
  const key = focus || '';
  let p = _tools.get(key);
  if (!p) {
    const qs = focus ? `?focus=${encodeURIComponent(focus)}` : '';
    p = fetch(apiUrl(`/threads/tools${qs}`), { headers })
      .then(async (res) => {
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json();
        return (Array.isArray(data?.tools) ? data.tools : []).map((t: any) => ({
          kind: 'tool' as const,
          id: String(t.id),
          label: String(t.label || t.id),
          description: t.description ? String(t.description) : undefined,
        }));
      })
      .catch(() => {
        _tools.delete(key);
        return [] as MentionItem[];
      });
    _tools.set(key, p);
  }
  return p;
}

/** Solo para la vista previa de desarrollo: olvida lo cargado. */
export function resetMentionCaches() {
  _skills = null;
  _tools.clear();
}
